/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Agent health", description = "Monitor agent pipeline runs, channel delivery rates, and outcome data freshness", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Agent health",
        description: "Monitor agent pipeline runs, channel delivery rates, and outcome data freshness",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Message generation",
          "depth": 0,
          "id": "message-generation"
        }, {
          "value": "View details",
          "depth": 1,
          "id": "view-details"
        }, {
          "value": "Channel send success",
          "depth": 0,
          "id": "channel-send-success"
        }, {
          "value": "Troubleshoot low delivery rates",
          "depth": 1,
          "id": "troubleshoot-low-delivery-rates"
        }, {
          "value": "Outcome health",
          "depth": 0,
          "id": "outcome-health"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      code: "code",
      img: "img",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A running ", _jsx(_components.a, {
                    href: "/ai-decisioning/agents",
                    children: "agent"
                  })]
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/ai-decisioning/configuration#configure-send-tracking",
                    children: "Send event tracking"
                  }), " configured for channel delivery metrics"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "The Health tab gives you real-time visibility into your agent's pipeline runs, channel delivery rates, and the freshness of the outcome data powering optimization."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "Health"
        }), " tab provides operational visibility into your agent from a single view. No additional setup is required — the tab automatically pulls data from your existing agent configuration, runs, and event models."]
      }), "\n", _jsxs(_components.p, {
        children: ["To access it, open an agent and go to the ", _jsx(_components.strong, {
          children: "Health"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/agents/aid-health-tab.png",
          alt: "Health tab overview"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "message-generation",
        children: "Message generation"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Message generation"
        }), " section shows the current and historical state of your agent's daily runs — the runs that schedule and produce AID messages."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Current run:"
        }), " If a generation is in progress, you can see when it started and how long it has been running. To see its current pipeline step, click ", _jsx(_components.strong, {
          children: "View details"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Last run:"
        }), " Shows when the most recent generation completed, how long it took, and how many recommendations were produced."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Historical runs:"
        }), " A table of recent runs with start time, duration, run status, and recommendation count."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Failed runs:"
        }), " If a run encountered an error, it is flagged in red. Click ", _jsx(_components.strong, {
          children: "View details"
        }), " to see per-stage durations and actionable error messages."]
      }), "\n", _jsx(_components.h3, {
        id: "view-details",
        children: "View details"
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "View details"
        }), " on any run to open a per-stage pipeline breakdown. This view is useful for diagnosing run failures, identifying bottlenecks, and understanding where time is spent across the generation process."]
      }), "\n", _jsx(_components.p, {
        children: "Each run includes the following pipeline phases:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cache Interactions"
          }), " — Loads recent interaction data"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Assign User Groups"
          }), " — Assigns users to treatment or holdout groups"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pull Warehouse"
          }), " — Retrieves data from your warehouse"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Generate Interactions"
          }), " — Produces message recommendations for eligible users"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Write Warehouse"
          }), " — Writes results back to your warehouse"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Compact Interactions"
          }), " — Cleans up interaction records"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Trigger Analytics"
          }), " — Updates reporting and analytics data"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Each phase shows its duration and status (", _jsx(_components.code, {
          children: "Completed"
        }), ", ", _jsx(_components.code, {
          children: "In progress"
        }), ", ", _jsx(_components.code, {
          children: "Pending"
        }), ", or ", _jsx(_components.code, {
          children: "Failed"
        }), "). Stages that haven't started yet show as ", _jsx(_components.code, {
          children: "Pending"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/agents/aid-health-details.png",
          alt: "Pipeline phase details"
        })
      }), "\n", _jsx(_components.p, {
        children: "When a run fails, the error message includes guidance on what went wrong and what you can do to fix it. Errors fall into two categories:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "External issues (actionable by you):"
          }), " Problems originating from your data or configuration. For example, a column may have been dropped or renamed in your warehouse while the agent's parent model, user feature matrix, audience, or event goals still reference it. Update the affected configuration and re-run the agent."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Internal issues:"
          }), " Problems on Hightouch's side. Hightouch receives internal alerting for all agent run failures and proactively investigates. You can also reach out to your Hightouch contact for support."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If a run fails at ", _jsx(_components.strong, {
            children: "Pull Warehouse"
          }), ", check that your data warehouse is\nrunning and accessible, and verify that your connection credentials in\nHightouch are correct."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "A common cause of run failures is a column being dropped or renamed in your\nwarehouse while the agent's audience, feature matrix, event models, or parent\nmodel still reference it. If you see a missing column error, check each of\nthose configurations for stale references, update them to reflect the current\nschema, and re-run the agent."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "channel-send-success",
        children: "Channel send success"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Send event tracking"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Channel send success"
        }), " section shows how successfully your agent's messages are being delivered to users, broken down by channel (Email, SMS, Push, and others)."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Send success:"
          }), " The percentage of scheduled messages confirmed sent by your ESP, based on the send event model configured for each channel."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sent, dropped, failed, and unverified counts:"
          }), " When reconciliation data is available, columns break down how many messages were sent, dropped or failed at sync, or remain unverified."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Syncing to:"
          }), " Shows which destination is handling delivery for that channel."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Time filter:"
          }), " Use the dropdown in the top-right corner to filter by ", _jsx(_components.strong, {
            children: "Last 30 days"
          }), ", ", _jsx(_components.strong, {
            children: "Last 60 days"
          }), ", or ", _jsx(_components.strong, {
            children: "Last 90 days"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/agents/aid-send-success-insights.png",
          alt: "Send success insights"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If send success is not configured for a channel, click ", _jsx(_components.strong, {
          children: "Open send success insights"
        }), " or go to ", _jsx(_components.a, {
          href: "/ai-decisioning/insights",
          children: "Insights > Send success"
        }), " to configure it."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Send success tracking requires send events to be linked to your channels. See\n", _jsx(_components.a, {
            href: "/ai-decisioning/configuration#configure-send-tracking",
            children: "Send event tracking"
          }), "\nfor setup instructions."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "troubleshoot-low-delivery-rates",
        children: "Troubleshoot low delivery rates"
      }), "\n", _jsx(_components.p, {
        children: "If you see unexpected delivery numbers, here are the most common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "\"Not configured\":"
          }), " No send event model is set for that channel. Configure send event tracking so Hightouch can match recommendations to confirmed sends."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "\"No data\":"
          }), " Send event tracking is configured, but no data has appeared yet. Data typically appears within 24 hours of configuration. If it was configured more than 24 hours ago, review the event model for that channel."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Below 50% (highlighted in red):"
          }), " This threshold flags channels that may have a misconfigured event model or a delivery issue worth investigating. This is particularly common during initial setup, when send tracking is first being validated."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "outcome-health",
        children: "Outcome health"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Outcome health"
        }), " section tracks the freshness of the event data powering each of your agent's configured outcomes (goals). This helps you catch reverse ETL pipeline issues before they affect agent optimization."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recent timestamp:"
          }), " Outcome event data is flowing correctly."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Stale or missing timestamp (\"--\"):"
          }), " Event data has not been loaded recently. Investigate whether the reverse ETL pipeline feeding that outcome has stalled or broken."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Each outcome's \"Last data loaded\" timestamp reflects when event data was most\nrecently ingested, not when the agent last ran. If you see a stale timestamp,\ncheck your reverse ETL sync for that outcome before the next agent run."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Issue"
            }), _jsx(_components.th, {
              children: "Likely cause"
            }), _jsx(_components.th, {
              children: "Resolution"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Channel send success shows \"Not configured\" or \"No data\""
            }), _jsx(_components.td, {
              children: "No send event model is set for that channel, or data hasn't appeared yet."
            }), _jsx(_components.td, {
              children: "Confirm the event model tracking send confirmations from your ESP is correctly configured and scoped to the relevant business units or campaign identifiers for your AID campaigns."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Outcome health shows a stale or missing timestamp"
            }), _jsx(_components.td, {
              children: "The reverse ETL pipeline feeding that outcome has stalled or broken."
            }), _jsx(_components.td, {
              children: "Check the reverse ETL sync for the affected outcome and resolve any pipeline issues before the next agent run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Pipeline run fails at Pull Warehouse"
            }), _jsx(_components.td, {
              children: "Your data warehouse is unreachable or credentials are invalid."
            }), _jsx(_components.td, {
              children: "Verify that your warehouse is running and accessible, and confirm your connection credentials in Hightouch are correct."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Pipeline run fails with a missing column error"
            }), _jsx(_components.td, {
              children: "A column was dropped or renamed in your warehouse while your parent model, audience, feature matrix, or event models still reference it."
            }), _jsx(_components.td, {
              children: "Update the affected configurations to reflect the current schema, then re-run the agent."
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/agent-alerting",
            children: "Agent alerting"
          }), " to get notified about run failures and volume drops."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/insights",
            children: "Insights"
          }), " to measure agent and message performance."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/agents",
            children: "Agents"
          }), " to configure goals, audiences, and scheduling."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
